import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Archive, 
  Plus, 
  Trash2, 
  Edit, 
  X, 
  Check, 
  FileText,
  BookOpen,
  Lightbulb,
  Filter,
  Search,
  Quote,
  Heart,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Frases de autores famosos para inspiração
const famousQuotes = [
  { text: "Um escritor é alguém para quem escrever é mais difícil do que para as outras pessoas.", author: "Thomas Mann" },
  { text: "Não existe grande talento sem grande força de vontade.", author: "Honoré de Balzac" },
  { text: "Escrever é fácil. Tudo o que você precisa fazer é sentar em frente à máquina de escrever e sangrar.", author: "Ernest Hemingway" },
  { text: "A primeira versão de qualquer coisa é uma merda.", author: "Ernest Hemingway" },
  { text: "Você pode sempre editar uma página ruim. Você não pode editar uma página em branco.", author: "Jodi Picoult" },
  { text: "Se você quer ser escritor, você deve fazer duas coisas acima de todas as outras: ler muito e escrever muito.", author: "Stephen King" }
];

export default function ArquivoIntelectual() {
  // Main tab state
  const [mainTab, setMainTab] = useState('arquivo'); // 'arquivo' or 'frases'
  
  // Arquivo states
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    item_type: 'prompt'
  });

  // Frases states
  const [phrases, setPhrases] = useState([]);
  const [frasesLoading, setFrasesLoading] = useState(false);
  const [showFraseForm, setShowFraseForm] = useState(false);
  const [editingFraseId, setEditingFraseId] = useState(null);
  const [frasesTab, setFrasesTab] = useState('minhas');
  const [fraseFormData, setFraseFormData] = useState({
    text: '',
    author: '',
    source: ''
  });

  const itemTypes = [
    { 
      value: 'prompt', 
      label: 'Prompt utilizável', 
      icon: FileText,
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      description: 'Prompts criativos para escrita ou geração de conteúdo'
    },
    { 
      value: 'manuscript', 
      label: 'Manuscrito livro', 
      icon: BookOpen,
      color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      description: 'Trechos de texto que fazem parte de um livro ou projeto'
    },
    { 
      value: 'future_idea', 
      label: 'Ideias uso futuro', 
      icon: Lightbulb,
      color: 'bg-green-500/10 text-green-600 dark:text-green-400',
      description: 'Ideias para desenvolver posteriormente'
    }
  ];

  useEffect(() => {
    fetchItems();
  }, [activeFilter]);

  useEffect(() => {
    if (mainTab === 'frases') {
      fetchPhrases();
    }
  }, [mainTab]);

  const fetchItems = async () => {
    try {
      let url = `${API_URL}/api/bau/arquivo`;
      if (activeFilter !== 'all') {
        url += `?item_type=${activeFilter}`;
      }
      const response = await axios.get(url, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Erro ao carregar arquivo intelectual');
    } finally {
      setLoading(false);
    }
  };

  // Frases functions
  const fetchPhrases = async () => {
    setFrasesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/phrases`, {
        withCredentials: true
      });
      setPhrases(response.data);
    } catch (error) {
      console.error('Error fetching phrases:', error);
      toast.error('Erro ao carregar frases');
    } finally {
      setFrasesLoading(false);
    }
  };

  const handleFraseSubmit = async (e) => {
    e.preventDefault();
    if (!fraseFormData.text.trim()) {
      toast.error('O texto da frase é obrigatório');
      return;
    }
    try {
      if (editingFraseId) {
        await axios.put(`${API_URL}/api/phrases/${editingFraseId}`, fraseFormData, { withCredentials: true });
        toast.success('Frase atualizada!');
      } else {
        await axios.post(`${API_URL}/api/phrases`, fraseFormData, { withCredentials: true });
        toast.success('Frase salva!');
      }
      setFraseFormData({ text: '', author: '', source: '' });
      setShowFraseForm(false);
      setEditingFraseId(null);
      fetchPhrases();
    } catch (error) {
      console.error('Error saving phrase:', error);
      toast.error('Erro ao salvar frase');
    }
  };

  const handleDeleteFrase = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta frase?')) return;
    try {
      await axios.delete(`${API_URL}/api/phrases/${id}`, { withCredentials: true });
      toast.success('Frase excluída');
      fetchPhrases();
    } catch (error) {
      console.error('Error deleting phrase:', error);
      toast.error('Erro ao excluir frase');
    }
  };

  const handleToggleFavorite = async (phrase) => {
    try {
      await axios.put(`${API_URL}/api/phrases/${phrase.phrase_id}`, 
        { ...phrase, is_favorite: !phrase.is_favorite }, 
        { withCredentials: true }
      );
      fetchPhrases();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      if (editingId) {
        await axios.put(
          `${API_URL}/api/bau/arquivo/${editingId}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Item atualizado!');
      } else {
        await axios.post(
          `${API_URL}/api/bau/arquivo`,
          formData,
          { withCredentials: true }
        );
        toast.success('Item salvo no arquivo!');
      }
      
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Erro ao salvar item');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', content: '', item_type: 'prompt' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description || '',
      content: item.content || '',
      item_type: item.item_type
    });
    setEditingId(item.item_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/bau/arquivo/${id}`, {
        withCredentials: true
      });
      toast.success('Item excluído do arquivo');
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Erro ao excluir item');
    }
  };

  const getTypeInfo = (type) => {
    return itemTypes.find(t => t.value === type) || itemTypes[0];
  };

  const getTypeBadge = (type) => {
    const typeInfo = getTypeInfo(type);
    const Icon = typeInfo.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
        <Icon size={12} />
        {typeInfo.label}
      </span>
    );
  };

  // Filter items by search term
  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="arquivo-intelectual-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-light mb-2">Baú Intelectual</h1>
              <p className="text-muted-foreground text-sm md:text-base">
                Guarde manuscritos, ideias, frases e inspirações
              </p>
            </div>
          </div>

          {/* Main Tabs: Arquivo / Frases */}
          <div className="flex gap-2 mb-6 border-b border-border">
            <button
              onClick={() => setMainTab('arquivo')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                mainTab === 'arquivo'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Archive size={16} className="inline mr-2" />
              Arquivo
            </button>
            <button
              onClick={() => setMainTab('frases')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                mainTab === 'frases'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Quote size={16} className="inline mr-2" />
              Frases Autorais
            </button>
          </div>

          {/* Arquivo Tab Content */}
          {mainTab === 'arquivo' && (
            <>
              {/* Add Button */}
              <div className="flex justify-end mb-4">
                <Button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                  data-testid="add-item-button"
                >
                  <Plus className="mr-2" size={20} />
                  Novo Item
                </Button>
              </div>

          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar por título..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeFilter === 'all'
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid="filter-all"
            >
              <Filter size={14} className="inline mr-1.5" />
              Todos ({filteredItems.length})
            </button>
            {itemTypes.map((type) => {
              const Icon = type.icon;
              const count = activeFilter === 'all' 
                ? items.filter(i => i.item_type === type.value).length 
                : (activeFilter === type.value ? items.length : 0);
              return (
                <button
                  key={type.value}
                  onClick={() => setActiveFilter(type.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeFilter === type.value
                      ? type.color + ' ring-2 ring-offset-2 ring-amber-500'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  data-testid={`filter-${type.value}`}
                >
                  <Icon size={14} className="inline mr-1.5" />
                  {type.label}
                </button>
              );
            })}
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-card border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-serif">
                    {editingId ? 'Editar Item' : 'Novo Item'}
                  </h2>
                  <button 
                    onClick={resetForm}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="close-form-button"
                  >
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Tipo do Item */}
                  <div>
                    <label className="block text-sm font-medium mb-3">Tipo do Item *</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {itemTypes.map((type) => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, item_type: type.value })}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              formData.item_type === type.value
                                ? 'border-amber-500 bg-amber-500/5'
                                : 'border-border hover:border-amber-500/50'
                            }`}
                            data-testid={`type-${type.value}`}
                          >
                            <Icon size={24} className={formData.item_type === type.value ? 'text-amber-500' : 'text-muted-foreground'} />
                            <p className="font-medium mt-2 text-sm">{type.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Título */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Título *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Dê um nome ao seu item..."
                      data-testid="item-title-input"
                    />
                  </div>

                  {/* Descrição/Objetivo */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {formData.item_type === 'prompt' ? 'Objetivo' : 'Descrição'}
                    </label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={formData.item_type === 'prompt' 
                        ? 'Para que serve este prompt?' 
                        : 'Breve descrição do conteúdo...'}
                      data-testid="item-description-input"
                    />
                  </div>

                  {/* Conteúdo */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Conteúdo</label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder={
                        formData.item_type === 'prompt' 
                          ? 'Escreva o prompt aqui...'
                          : formData.item_type === 'manuscript'
                          ? 'Escreva o texto do manuscrito...'
                          : 'Descreva sua ideia em detalhes...'
                      }
                      rows={8}
                      className="font-mono text-sm"
                      data-testid="item-content-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-stone-900 flex-1"
                      data-testid="save-item-button"
                    >
                      <Check className="mr-2" size={18} />
                      {editingId ? 'Salvar Alterações' : 'Salvar no Arquivo'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Items List */}
          {filteredItems.length === 0 && searchTerm ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-lg">
              <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-medium mb-2">Nenhum resultado encontrado</h3>
              <p className="text-muted-foreground mb-6">
                Não encontramos itens com "{searchTerm}" no título.
              </p>
              <Button
                onClick={() => setSearchTerm('')}
                variant="outline"
              >
                Limpar pesquisa
              </Button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-lg">
              <Archive className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-medium mb-2">
                {activeFilter === 'all' 
                  ? 'Arquivo vazio' 
                  : `Nenhum ${getTypeInfo(activeFilter).label.toLowerCase()}`}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {activeFilter === 'all'
                  ? 'Seu arquivo intelectual guarda as ideias que moldam sua escrita.'
                  : `Você ainda não tem ${getTypeInfo(activeFilter).label.toLowerCase()} salvos.`}
              </p>
              <Button
                onClick={() => {
                  if (activeFilter !== 'all') {
                    setFormData({ ...formData, item_type: activeFilter });
                  }
                  setShowForm(true);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                <Plus className="mr-2" size={18} />
                Criar Primeiro Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredItems.map((item) => {
                const typeInfo = getTypeInfo(item.item_type);
                const Icon = typeInfo.icon;
                return (
                  <div
                    key={item.item_id}
                    className="bg-card border border-border/40 rounded-lg p-5 hover:shadow-md transition-all"
                    data-testid={`item-card-${item.item_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                            <Icon size={18} />
                          </div>
                          <h3 className="text-lg font-medium truncate">{item.title}</h3>
                        </div>
                        
                        {getTypeBadge(item.item_type)}
                        
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-3">
                            {item.description}
                          </p>
                        )}
                        
                        {item.content && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-md">
                            <p className="text-sm text-foreground/80 line-clamp-3 whitespace-pre-wrap">
                              {item.content}
                            </p>
                          </div>
                        )}
                        
                        <p className="text-xs text-muted-foreground mt-3">
                          Criado em {new Date(item.created_at).toLocaleDateString('pt-BR')}
                          {item.updated_at !== item.created_at && (
                            <> · Editado em {new Date(item.updated_at).toLocaleDateString('pt-BR')}</>
                          )}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                          data-testid={`edit-item-${item.item_id}`}
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.item_id)}
                          className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                          data-testid={`delete-item-${item.item_id}`}
                          title="Excluir"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Conceptual message */}
          {mainTab === 'arquivo' && items.length > 0 && (
            <p className="text-center text-sm text-muted-foreground mt-8 italic">
              "Aqui vivem os pensamentos que ainda se tornarão livros."
            </p>
          )}
            </>
          )}

          {/* Frases Tab Content */}
          {mainTab === 'frases' && (
            <>
              {/* Frases Sub-tabs */}
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setFrasesTab('minhas')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    frasesTab === 'minhas'
                      ? 'bg-amber-500 text-stone-900'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <User size={14} className="inline mr-1.5" />
                  Minhas Frases
                </button>
                <button
                  onClick={() => setFrasesTab('inspiracao')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    frasesTab === 'inspiracao'
                      ? 'bg-amber-500 text-stone-900'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <BookOpen size={14} className="inline mr-1.5" />
                  Inspiração
                </button>
              </div>

              {/* Minhas Frases */}
              {frasesTab === 'minhas' && (
                <>
                  <div className="flex justify-end mb-4">
                    <Button
                      onClick={() => {
                        setFraseFormData({ text: '', author: '', source: '' });
                        setShowFraseForm(true);
                        setEditingFraseId(null);
                      }}
                      className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                    >
                      <Plus className="mr-2" size={20} />
                      Nova Frase
                    </Button>
                  </div>

                  {/* Frase Form Modal */}
                  {showFraseForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium">
                            {editingFraseId ? 'Editar Frase' : 'Nova Frase'}
                          </h3>
                          <button onClick={() => setShowFraseForm(false)} className="text-muted-foreground hover:text-foreground">
                            <X size={20} />
                          </button>
                        </div>
                        <form onSubmit={handleFraseSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Frase *</label>
                            <Textarea
                              value={fraseFormData.text}
                              onChange={(e) => setFraseFormData({ ...fraseFormData, text: e.target.value })}
                              placeholder="Digite sua frase..."
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Autor</label>
                            <Input
                              value={fraseFormData.author}
                              onChange={(e) => setFraseFormData({ ...fraseFormData, author: e.target.value })}
                              placeholder="Autor da frase"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Fonte</label>
                            <Input
                              value={fraseFormData.source}
                              onChange={(e) => setFraseFormData({ ...fraseFormData, source: e.target.value })}
                              placeholder="Livro, artigo, etc."
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button type="button" variant="outline" onClick={() => setShowFraseForm(false)}>
                              Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-stone-900">
                              {editingFraseId ? 'Salvar' : 'Criar'}
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                  {/* Frases List */}
                  {frasesLoading ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-24 bg-muted rounded"></div>
                      <div className="h-24 bg-muted rounded"></div>
                    </div>
                  ) : phrases.length === 0 ? (
                    <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
                      <Quote className="mx-auto mb-4 text-muted-foreground" size={40} />
                      <h3 className="text-lg font-medium mb-2">Nenhuma frase ainda</h3>
                      <p className="text-muted-foreground text-sm">
                        Comece a colecionar suas frases favoritas
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {phrases.map((phrase) => (
                        <div key={phrase.phrase_id} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <Quote className="text-amber-500 flex-shrink-0 mt-1" size={20} />
                            <div className="flex-1">
                              <p className="text-foreground italic mb-2">"{phrase.text}"</p>
                              {phrase.author && (
                                <p className="text-sm text-muted-foreground">— {phrase.author}</p>
                              )}
                              {phrase.source && (
                                <p className="text-xs text-muted-foreground mt-1">Fonte: {phrase.source}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleToggleFavorite(phrase)}
                                className={`p-2 rounded-md transition-colors ${
                                  phrase.is_favorite
                                    ? 'text-red-500 hover:text-red-600'
                                    : 'text-muted-foreground hover:text-red-500'
                                }`}
                              >
                                <Heart size={18} fill={phrase.is_favorite ? 'currentColor' : 'none'} />
                              </button>
                              <button
                                onClick={() => {
                                  setFraseFormData({ text: phrase.text, author: phrase.author || '', source: phrase.source || '' });
                                  setEditingFraseId(phrase.phrase_id);
                                  setShowFraseForm(true);
                                }}
                                className="p-2 text-muted-foreground hover:text-foreground rounded-md transition-colors"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteFrase(phrase.phrase_id)}
                                className="p-2 text-muted-foreground hover:text-destructive rounded-md transition-colors"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Inspiração Tab */}
              {frasesTab === 'inspiracao' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground mb-4">
                    Frases de autores consagrados para inspirar sua escrita
                  </p>
                  {famousQuotes.map((quote) => (
                    <div key={`quote-${quote.author}`} className="bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/20 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Quote className="text-amber-500 flex-shrink-0 mt-1" size={20} />
                        <div>
                          <p className="text-foreground italic mb-2">"{quote.text}"</p>
                          <p className="text-sm text-amber-600 dark:text-amber-400">— {quote.author}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
